'use client';

import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
  useMemo,
  ReactNode,
} from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { saveScrollPos } from '@hooks/restoreScrollPosition/storage';
import { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';

type NavigationStatus = 'initial' | 'forward' | 'back' | 'reload' | 'show';

export interface CustomNavigateOptions extends NavigateOptions {
  forceReload?: boolean;
  parallel?: boolean;
}

interface NavigationContextType {
  navigationStatus: NavigationStatus;
}

interface NavigationProviderProps {
  children: ReactNode;
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined
);

export function useNavigation(): NavigationContextType {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}

const browserHistory =
  typeof window !== 'undefined'
    ? {
        pushState: window.history.pushState.bind(window.history),
        addEventListener: window.addEventListener.bind(window),
        removeEventListener: window.removeEventListener.bind(window),
      }
    : null;

export function NavigationProvider({ children }: NavigationProviderProps) {
  const [navigationStatus, setNavigationStatus] =
    useState<NavigationStatus>('initial');
  const isBack = useRef(false);
  const router = useRouter();
  const pathname = usePathname();

  const checkNavigationType = useCallback((): NavigationStatus => {
    if (typeof window === 'undefined') return 'show';

    if (performance && performance.navigation) {
      switch (performance.navigation.type) {
        case performance.navigation.TYPE_NAVIGATE:
          return 'forward';
        case performance.navigation.TYPE_RELOAD:
          return 'reload';
        case performance.navigation.TYPE_BACK_FORWARD:
          return 'back';
        default:
          return 'show';
      }
    } else {
      return 'show';
    }
  }, []);

  const onPageShow = useCallback(
    (e: PageTransitionEvent) => {
      if (e.persisted) {
        setNavigationStatus('back');
      } else {
        const status = checkNavigationType();
        setNavigationStatus(status);
      }
    },
    [checkNavigationType]
  );

  const onRouteChangeStart = useCallback((): void => {
    if (!isBack.current) {
      setNavigationStatus('forward');
    }
  }, []);

  const onPopState = useCallback(() => {
    setNavigationStatus('back');
  }, []);

  useEffect(() => {
    browserHistory?.addEventListener('pageshow', onPageShow);
    browserHistory?.addEventListener('popstate', onPopState);

    const initialStatus = checkNavigationType();
    setNavigationStatus(initialStatus);

    return () => {
      browserHistory?.removeEventListener('pageshow', onPageShow);
      browserHistory?.removeEventListener('popstate', onPopState);
    };
  }, [onPageShow, onPopState, checkNavigationType]);

  useEffect(() => {
    const originalPushState = browserHistory?.pushState;
    if (browserHistory) {
      browserHistory.pushState = function (...args) {
        originalPushState?.apply(this, args);
        onRouteChangeStart();
      };
    }

    return () => {
      if (browserHistory && originalPushState) {
        browserHistory.pushState = originalPushState;
      }
    };
  }, [onRouteChangeStart]);

  useEffect(() => {
    // 사파리 이슈 때문에 router.push 대신 location.href 사용
    // confrim alert이 물리키 및 스와이프로 뒤로가기하고 다시 해당 페이지 들어가면
    // 노출이 안됨 관련 이슈: https://wck.atlassian.net/browse/ITDEV-17801

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const originalPush = router.push;
    // eslint-disable-next-line func-names
    router.push = function (href: string, options?: CustomNavigateOptions) {
      saveScrollPos(`${window.location.pathname}`);
      onRouteChangeStart();
      if (options?.forceReload) {
        window.location.href = href;
        return Promise.resolve(true);
      }
      return originalPush.apply(this, [href, options]);
    };

    return () => {
      router.push = originalPush;
    };
  }, [router, onRouteChangeStart]);

  const value = useMemo(() => ({ navigationStatus }), [navigationStatus]);

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}
